import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Star, Calendar, HardDrive, Tag, CheckCircle2, ExternalLink, FileCode, FileText, ArrowLeft, Loader2, X } from "lucide-react";
import { PublicLayout } from "../components/Common";
import { useAuth } from "../context/AuthContext";
import api, { API } from "../lib/api";
import { formatIDR, mediaUrl, typeLabel, formatDate } from "../lib/format";

export default function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [tab, setTab] = useState("desc");
  const [busy, setBusy] = useState("");
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    setP(null);
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      document.title = `${r.data.title} - DS KODE`;
    }).catch(() => setP(false));
  }, [slug]);

  useEffect(() => {
    if (!lightboxImage) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setLightboxImage(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = "";
    };
  }, [lightboxImage]);

  if (p === false) {
    return <PublicLayout><div className="max-w-7xl mx-auto px-4 py-20 text-center"><h2 className="font-heading text-2xl font-bold">Produk tidak ditemukan</h2><Link to="/products" className="text-indigo-600 mt-3 inline-block">Kembali ke katalog</Link></div></PublicLayout>;
  }
  if (!p) {
    return <PublicLayout><div className="max-w-7xl mx-auto px-4 py-10"><div className="aspect-video max-w-3xl skeleton rounded-xl" /></div></PublicLayout>;
  }

  const gallery = (p.gallery && p.gallery.length ? p.gallery : [p.thumbnail]).filter(Boolean);

  const openExternal = (url) => {
    if (!user) { navigate("/login", { state: { from: location.pathname } }); return; }
    window.open(url, "_blank", "noreferrer");
  };

  const doDownload = async (type) => {
    if (!user) { navigate("/login", { state: { from: location.pathname } }); return; }
    if (user.role !== "admin" && user.status !== "APPROVED") { toast.error("Akun Anda belum disetujui admin."); return; }
    setBusy(type);
    setDownloadProgress(0);
    try {
      const res = await api.get(`/download/${p.id}?type=${type}`, {
        responseType: "blob",
        onDownloadProgress: (event) => {
          if (event.total) setDownloadProgress(Math.round((event.loaded * 100) / event.total));
        },
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const fallbackExt = type === "DOCUMENTATION" ? ".pdf" : ".zip";
      const fname = match ? decodeURIComponent(match[1].replace(/"$/, "")) : `${p.slug}-${type.toLowerCase()}${fallbackExt}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
      toast.success("Download berhasil");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download gagal");
    } finally {
      setBusy("");
    }
  };

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-5"><ArrowLeft className="h-4 w-4" /> Kembali</button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left */}
          <div className="lg:col-span-2">
            <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
              <button type="button" onClick={() => setLightboxImage(gallery[active])} className="group block w-full cursor-zoom-in" aria-label="Lihat gambar utuh">
                <img src={mediaUrl(gallery[active])} alt={p.title} className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-[1.02]" data-testid="product-main-image" />
              </button>
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setActive(i)} className={`h-16 w-24 shrink-0 rounded-lg overflow-hidden border-2 ${active === i ? "border-indigo-600" : "border-transparent"}`}>
                    <img src={mediaUrl(g)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Tabs */}
            <div className="mt-8 border-b border-slate-200 flex gap-1">
              {[["desc", "Deskripsi & Fitur"], ["tech", "Spesifikasi"], ["changelog", "Changelog"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{l}</button>
              ))}
            </div>

            <div className="py-6">
              {tab === "desc" && (
                <div>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">{p.description}</p>
              {lightboxImage && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/90 p-4 sm:p-8" role="dialog" aria-modal="true" aria-label="Pratinjau gambar produk" onClick={() => setLightboxImage(null)}>
                  <button type="button" onClick={() => setLightboxImage(null)} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20" aria-label="Tutup pratinjau">
                    <X className="h-6 w-6" />
                  </button>
                  <img src={mediaUrl(lightboxImage)} alt={p.title} className="max-h-[92vh] max-w-[96vw] object-contain" onClick={(event) => event.stopPropagation()} />
                </div>
              )}
                  {p.features?.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-heading font-semibold text-slate-900 mb-3">Fitur Utama</h3>
                      <ul className="grid sm:grid-cols-2 gap-2">
                        {p.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {f}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {tab === "tech" && (
                <dl className="space-y-3 text-sm">
                  <Spec label="Teknologi" value={<div className="flex flex-wrap gap-1.5">{(p.technology || []).map((t) => <span key={t} className="font-mono text-xs px-2 py-0.5 rounded bg-slate-100 border border-slate-200">{t}</span>)}</div>} />
                  <Spec label="Jenis Produk" value={typeLabel(p.product_type)} />
                  <Spec label="Versi" value={p.version} />
                  <Spec label="Ukuran File" value={p.file_size || "Belum tersedia"} />
                  <Spec label="Terakhir Update" value={formatDate(p.updated_at)} />
                </dl>
              )}
              {tab === "changelog" && <pre className="text-sm text-slate-600 whitespace-pre-wrap font-sans leading-relaxed">{p.changelog || "Belum ada changelog."}</pre>}
            </div>
          </div>

          {/* Right sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 bg-white rounded-xl border border-slate-200 p-6">
              <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">{p.category_name}</p>
              <h1 className="font-heading text-2xl font-extrabold text-slate-900 mt-1 leading-tight">{p.title}</h1>
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {p.rating}</span>
                <span className="flex items-center gap-1"><Download className="h-4 w-4" /> {p.download_count} unduhan</span>
              </div>

              <div className="mt-5 pt-5 border-t border-slate-100">
                <p className={`font-heading text-3xl font-extrabold ${p.price === 0 ? "text-emerald-600" : "text-slate-900"}`}>{formatIDR(p.price)}</p>
              </div>

              <div className="mt-5 space-y-2.5">
                {p.application_file && <button onClick={() => doDownload("APPLICATION")} disabled={busy} data-testid={`btn-download-product-${p.id}`} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors disabled:opacity-60">
                  {busy === "APPLICATION" ? <><Loader2 className="h-4 w-4 animate-spin" /> Mengunduh... {downloadProgress}%</> : <><Download className="h-4 w-4" /> Download Aplikasi</>}
                </button>}
                {p.source_code_file && <button onClick={() => doDownload("SOURCE_CODE")} disabled={busy} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-sm transition-colors">
                  {busy === "SOURCE_CODE" ? <><Loader2 className="h-4 w-4 animate-spin" /> Mengunduh... {downloadProgress}%</> : <><FileCode className="h-4 w-4" /> Source Code</>}
                </button>}
                {p.documentation_file && <button onClick={() => doDownload("DOCUMENTATION")} disabled={busy} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-sm transition-colors">
                  {busy === "DOCUMENTATION" ? <><Loader2 className="h-4 w-4 animate-spin" /> Mengunduh... {downloadProgress}%</> : <><FileText className="h-4 w-4" /> Dokumentasi</>}
                </button>}

                {p.demo_url && <button type="button" onClick={() => openExternal(p.demo_url)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-sm transition-colors"><ExternalLink className="h-4 w-4" /> Lihat Demo</button>}
                {p.download_url && <button type="button" onClick={() => openExternal(p.download_url)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold text-sm transition-colors hover:bg-emerald-100"><ExternalLink className="h-4 w-4" /> Link Download</button>}
              </div>

              {user && user.role !== "admin" && user.status !== "APPROVED" && (
                <p className="mt-3 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2.5">Akun Anda masih menunggu persetujuan admin untuk dapat mengunduh.</p>
              )}

              <dl className="mt-6 pt-5 border-t border-slate-100 space-y-2.5 text-sm">
                <MiniSpec icon={Tag} label="Versi" value={p.version} />
                <MiniSpec icon={HardDrive} label="Ukuran" value={p.file_size || "Belum tersedia"} />
                <MiniSpec icon={Calendar} label="Update" value={formatDate(p.updated_at)} />
              </dl>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

function Spec({ label, value }) {
  return <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2 border-b border-slate-100"><dt className="w-40 text-slate-500 font-medium">{label}</dt><dd className="text-slate-900">{value}</dd></div>;
}
function MiniSpec({ icon: Icon, label, value }) {
  return <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-slate-500"><Icon className="h-4 w-4" /> {label}</span><span className="font-medium text-slate-900">{value}</span></div>;
}
